import React, { FC } from 'react';
import styles from '../journey-explorer.module.scss';
import { MapMode } from '../journey-explorer-utils';

const MODES: { mode: MapMode; label: string; title: string }[] = [
	{ mode: 'customers', label: 'Customers', title: 'How many customers did each step' },
	{
		mode: 'median',
		label: 'Median time',
		title: 'Typical gap before the next step: half of customers are quicker, half slower',
	},
	{
		mode: 'time',
		label: 'Avg time',
		title: 'Average gap before the next step. A few customers who come back days later pull it up',
	},
];

const SLIDER_STEP = 1;

const SliderRow: FC<{ label: string; value: number; onChange: (value: number) => void }> = ({
	label,
	value,
	onChange,
}) => {
	const step = (delta: number) => onChange(Math.min(100, Math.max(0, value + delta)));
	return (
		<div className={`${styles.control_row} ${styles.slider_row}`}>
			{label}
			<button
				type="button"
				className={styles.slider_step}
				aria-label={`Show fewer ${label.toLowerCase()}`}
				title={`Fewer ${label.toLowerCase()}`}
				disabled={value <= 0}
				onClick={() => step(-SLIDER_STEP)}
			>
				−
			</button>
			<input
				type="range"
				min={0}
				max={100}
				value={value}
				aria-label={label}
				className={styles.slider}
				onChange={(event) => onChange(Number(event.target.value))}
			/>
			<button
				type="button"
				className={styles.slider_step}
				aria-label={`Show more ${label.toLowerCase()}`}
				title={`More ${label.toLowerCase()}`}
				disabled={value >= 100}
				onClick={() => step(SLIDER_STEP)}
			>
				+
			</button>
			<span className={styles.slider_value}>{value}%</span>
		</div>
	);
};

export interface MapControlsProps {
	mapMode: MapMode;
	interactionsPct: number;
	transitionsPct: number;
	stages: boolean | null;
	onMapModeChange: (mode: MapMode) => void;
	onInteractionsPctChange: (value: number) => void;
	onTransitionsPctChange: (value: number) => void;
	onStagesChange: (stages: boolean) => void;
}

const MapControls: FC<MapControlsProps> = ({
	mapMode,
	interactionsPct,
	transitionsPct,
	stages,
	onMapModeChange,
	onInteractionsPctChange,
	onTransitionsPctChange,
	onStagesChange,
}) => (
	<div className={styles.map_controls}>
		{stages !== null && (
			<div className={`${styles.control_row} ${styles.control_group}`}>
				<span className={styles.control_label}>Detail</span>
				<div className={styles.mode_options}>
					{[
						{ on: true, label: 'Groups' },
						{ on: false, label: 'Interactions' },
					].map(({ on, label }) => (
						<button
							key={label}
							type="button"
							aria-pressed={stages === on}
							title={on ? 'One box per interaction type' : 'Every interaction as its own box'}
							className={`${styles.mode_option} ${stages === on ? styles.mode_option_active : ''}`}
							onClick={() => onStagesChange(on)}
						>
							{label}
						</button>
					))}
				</div>
			</div>
		)}
		<div className={`${styles.control_row} ${styles.control_group}`}>
			<span className={styles.control_label}>Measure</span>
			<div className={styles.mode_options}>
				{MODES.map(({ mode, label, title }) => (
					<button
						key={mode}
						type="button"
						title={title}
						aria-pressed={mapMode === mode}
						className={`${styles.mode_option} ${mapMode === mode ? styles.mode_option_active : ''}`}
						onClick={() => onMapModeChange(mode)}
					>
						{label}
					</button>
				))}
			</div>
		</div>
		<div className={`${styles.control_row} ${styles.control_group} ${styles.slider_group}`}>
			<SliderRow label="Interactions" value={interactionsPct} onChange={onInteractionsPctChange} />
			<SliderRow label="Transitions" value={transitionsPct} onChange={onTransitionsPctChange} />
		</div>
	</div>
);

export default MapControls;
